import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Avatar,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import {
  Store,
  WbSunny,
  Apartment,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getActiveAnnouncement } from '../../services/announcement';
import { BRANDING } from '../../utils/branding';


// Import the separate dashboard components
import RealEstateDashboard from './RealEstateDashboard';
import RetailDashboard from './RetailDashboard';
import SolarEnergyDashboard from './SolarEnergyDashboard';


// Track if ad was shown in the current page lifecycle (resets on refresh, persists on navigation)
let adShownInCurrentCycle = false;

// Business Types Configuration
const businessTypes = {
  'real-estate': {
    name: 'Real Estate',
    icon: Apartment,
    color: '#6366f1', // Improved Indigo
    disabled: false
  },
  'retail': {
    name: 'Retail (Coming Soon)',
    icon: Store,
    color: '#10b981', // Improved Green
    disabled: true
  },
  'solar-energy': {
    name: 'Green Energy (Coming Soon)',
    icon: WbSunny,
    color: '#f59e0b', // Improved Amber
    disabled: true
  }
};

export default function Dashboard() {
  const [selectedBusiness, setSelectedBusiness] = useState('real-estate');
  const [activeAd, setActiveAd] = useState(null);
  const [showAd, setShowAd] = useState(false);
  const theme = useTheme();
  const { user } = useAuth();

  React.useEffect(() => {
    const fetchAd = async () => {
      // Show only once per page load/refresh (not on every internal navigation)
      if (adShownInCurrentCycle) return;

      // const role = user?.role?.toLowerCase();
      // if (role === 'admin' || role === 'superadmin') return;

      try {
        const response = await getActiveAnnouncement('popup');
        if (response.status && response.data && Object.keys(response.data).length > 0 && response.data._id) {
          setActiveAd(response.data);
          setShowAd(true);
          adShownInCurrentCycle = true;
        }
      } catch (err) {
        console.error('Failed to fetch active ad:', err);
      }
    };
    adShownInCurrentCycle = false;
    fetchAd();
  }, [user]);

  const handleBusinessChange = (event) => {
    setSelectedBusiness(event.target.value);
  };

  const renderDashboardContent = () => {
    switch (selectedBusiness) {
      case 'real-estate':
        return <RealEstateDashboard />;
      case 'retail':
        return <RetailDashboard />;
      case 'solar-energy':
        return <SolarEnergyDashboard />;
      default:
        return <RealEstateDashboard />;
    }
  };

  const currentBusiness = businessTypes[selectedBusiness];
  const IconComponent = currentBusiness.icon;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    }}>
      {/* Main Dashboard Transition */}
      <Box sx={{ px: { xs: 1, sm: 0 } }}>
        {renderDashboardContent()}
      </Box>



      {/* Active Announcement Popup */}
      <Dialog
        open={showAd}
        onClose={() => setShowAd(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            position: 'relative',
            p: 0,
            background: 'transparent',
            boxShadow: 'none'
          }
        }}
      >
        <Box sx={{ position: 'relative', backgroundColor: 'white', borderRadius: 4, overflow: 'hidden' }}>
          <IconButton
            onClick={() => setShowAd(false)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { backgroundColor: 'white' }
            }}
          >
            <CloseIcon />
          </IconButton>

          {activeAd && (
            <Box>
              <Box
                component="img"
                src={activeAd.imageUrl}
                alt={activeAd.title}
                sx={{
                  width: '100%',
                  display: 'block',
                  cursor: 'pointer'
                }}
              />
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  {activeAd.title}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}